import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { useAuth } from "../../lib/auth";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [attempts, setAttempts] = useState([]);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setStats(r.data));
    api.get("/attempts").then((r) => setAttempts(r.data));
  }, []);

  const submitted = attempts.filter((a) => a.status === "submitted");
  const dayBuckets = bucketByDay(submitted);

  const isSuper = user?.role === "super_admin";

  return (
    <div>
      <PageHeader
        overline={isSuper ? "Platform overview" : "Institution overview"}
        title={isSuper ? "All institutions" : "Operations dashboard"}
        subtitle="Real-time platform health, exams, and student activity."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 border border-zinc-200 mb-10" data-testid="admin-stats">
        {[
          { label: "Institutions", value: stats?.total_institutions, show: isSuper },
          { label: "Total Users", value: stats?.total_users, show: true },
          { label: "Students", value: stats?.total_students, show: true },
          { label: "Examiners", value: stats?.total_examiners, show: true },
          { label: "Exams", value: stats?.total_exams, show: true },
          { label: "Published", value: stats?.published_exams, show: true },
          { label: "Questions", value: stats?.total_questions, show: true },
          { label: "Attempts", value: stats?.total_attempts, show: true },
        ].filter((s) => s.show).map((s, i, arr) => (
          <div key={s.label} className={`p-5 ${i < arr.length - 1 ? "md:border-r border-zinc-200" : ""}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-semibold">{s.label}</div>
            <div className="font-display text-2xl font-bold tabular">{s.value ?? "…"}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-0 border border-zinc-200">
        <div className="lg:col-span-2 p-6 border-r border-zinc-200">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4 font-semibold">Exam attempts (last 7 days)</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dayBuckets}>
              <XAxis dataKey="day" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #e4e4e7" }} />
              <Bar dataKey="count" fill="#09090b" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="p-6">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4 font-semibold">Recent submissions</div>
          <div className="space-y-2 max-h-[260px] overflow-y-auto">
            {submitted.slice(0, 12).map((a) => (
              <div key={a.attempt_id} className="text-xs border-b border-zinc-100 pb-2">
                <div className="font-medium">{a.exam_title}</div>
                <div className="text-zinc-500 flex justify-between mt-0.5">
                  <span>Score: {a.score}/{a.total_marks}</span>
                  <span>{new Date(a.submitted_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
            {submitted.length === 0 && <div className="text-sm text-zinc-500">No submissions yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function bucketByDay(items) {
  const map = {};
  const today = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const k = d.toISOString().slice(5, 10);
    map[k] = 0;
  }
  items.forEach((a) => {
    if (!a.submitted_at) return;
    const k = new Date(a.submitted_at).toISOString().slice(5, 10);
    if (k in map) map[k]++;
  });
  return Object.entries(map).map(([day, count]) => ({ day, count }));
}
